import type { PinpointMappingType } from '@/features/comment/models/pinpoint';
import { PROJECT_BASE_DIR } from '@/features/project/_utils/constants';
import { generateQueryString } from '@/utils';
import { matchProjectDetailPath } from '@/utils/route-match';
import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { usePinpoint } from '../components/pinpoint';
import {
  type ReturnQueryParamsType,
  getQueryParamsByField,
} from '../utils/activity-tab-mapping-by-pipoint-field';

export const useNavigateToPinpoint = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentParams = Object.fromEntries(searchParams.entries());
  const { scrollToPinpoint } = usePinpoint();

  return useCallback(
    async ({
      objectId,
      pinpoint,
      newTab = false,
      commentId,
    }: {
      objectId: string;
      pinpoint?: PinpointMappingType | null | undefined;
      newTab?: boolean;
      commentId: string;
    }) => {
      // projectページ内でのリンク
      const queryString = buildQueryStringForPinpoint({
        pinpoint: pinpoint || undefined,
        currentParams,
        commentId: commentId ?? undefined,
        pinpointPathObj: pinpoint ? getQueryParamsByField(pinpoint) : undefined,
      });

      if (matchProjectDetailPath(location.pathname)) {
        openOrNavigate(
          `${PROJECT_BASE_DIR}/${objectId}${queryString}`,
          newTab,
          navigate,
        );

        if (pinpoint) {
          scrollToPinpoint(pinpoint);
        }
        return;
      }

      openOrNavigate(
        `${PROJECT_BASE_DIR}/${objectId}${queryString}`,
        newTab,
        navigate,
      );
      if (pinpoint) {
        scrollToPinpoint(pinpoint);
      }
      return;
    },
    [navigate, currentParams, scrollToPinpoint],
  );
};

const removeUndefined = <T extends Record<string, any>>(obj: T) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== null),
  ) as Partial<T>;

const buildQueryStringForPinpoint = ({
  pinpoint,
  currentParams,
  commentId,
  pinpointPathObj,
}: {
  pinpoint?: PinpointMappingType;
  currentParams?: Record<string, string>;
  commentId?: string;
  pinpointPathObj?: ReturnQueryParamsType;
}) => {
  const sceneParams = getQueryParamsByField(pinpoint);

  const mergedParams = removeUndefined({
    ...currentParams,
    ...sceneParams,
    ...pinpointPathObj,
    commentId,
  });

  return generateQueryString(mergedParams);
};

const openOrNavigate = async (
  path: string,
  newTab: boolean,
  navigate: ReturnType<typeof useNavigate>,
) => {
  if (newTab) {
    window.open(path, '_blank');
  } else {
    await navigate(path);
  }
};
